import {Module} from '@fantasmagames/slot/src/common/Module';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {SpinButtonEvents} from '@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonEvents';
import {AvalancheReelsEvents} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsEvents';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {SwipeInteractionEvents} from '@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionEvents';
import {SwipeInteractionView} from '@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionView';
import {SwipeInteractionViewEvents} from '@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionViewEvents';
import {InfoPopupEvents} from '@fantasmagames/slot/src/modules/popupmenus/info/InfoPopupEvents';
import {InfoPopupTypes} from '@fantasmagames/slot/src/modules/popupmenus/info/InfoPopupTypes';
import {SettingsEvents} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';

/**
 * handling of swipe interaction from user - starts spinning
 */
export class SwipeInteractionModule extends Module<SwipeInteractionView> {
	protected _isAutoSpinning: boolean = false;
	protected _isSwipeSpin: boolean = false;
	protected _isBonusGame: boolean = false;
	protected _isDragSettingActive: boolean;

	protected _isFreeSpins: boolean = false;

	protected _canSpin: boolean = true;

	protected setupStates(): void {
	}

	protected enterFirstState(): void {
	}

	protected setupViews(): void {
		if (this.system.shouldUseMultipleViews()) {
			this.setView(Views.SWIPE_INTERACTION_VIEW_MOBILE);
		} else {
			this.setView(Views.SWIPE_INTERACTION_VIEW);
		}
	}

	public init(): void {
		super.init();

		this.isDragSettingActive = this.logic.dragReelToSpinActive;
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.on(ReelsEvents.ALL_REELS_STOPPED, this.onAllReelsStopped);

		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinFinished);

		this.on(SpinButtonEvents.SPIN_BUTTON_BECOMING_VISIBLE_AND_ENABLED, this.onCanSpin);
		this.on(FlowModuleEvents.BONUS_GAME_WON, this.onBonusGameWon);
		this.on(FlowModuleEvents.BONUS_GAME_END, this.onBonusGameEnd);

		this.on(SpinButtonEvents.SPIN_BUTTON_PRESSED, this.onSpinButtonPressed);
		this.on(SpinButtonEvents.STOP_AUTOPLAY_BUTTON_PRESSED, this.onStopAutoplayButtonPressed);
		this.on(SpinButtonEvents.START_AUTOPLAY_BUTTON_PRESSED, this.onStartAutoplayButtonPressed);
		this.on(SpinButtonEvents.SPIN_PRESSED_NOT_ENOUGH_MONEY, this.onNotEnoughMoney);

		this.onView(SwipeInteractionViewEvents.START_SPIN, this.emitStartSpin);
		this.onViewEmitEvent(SwipeInteractionViewEvents.DRAGGING_STARTED, SwipeInteractionEvents.DRAGGING_STARTED);
		this.onViewEmitEvent(SwipeInteractionViewEvents.DRAGGING_FINISHED, SwipeInteractionEvents.DRAGGING_FINISHED);

		this.on(InfoPopupEvents.INFO_POPUP_CLOSED, this.onInfoPopupClosed);

		this.on(AvalancheReelsEvents.ALL_REELS_DROP_OUT_FINISHED, this.onAllReelsDropOutFinished);

		this.on(SettingsEvents.TOGGLE_DRAG_REEL_TO_SPIN_OFF, this.onSettingsToggleDragReelOff);
		this.on(SettingsEvents.TOGGLE_DRAG_REEL_TO_SPIN_ON, this.onSettingsToggleDragReelOn);
	}

	public restore(): void {
		super.restore();

		if (this.logic.isFreeSpinsStarted) {
			this._isFreeSpins = true;
			this.disableDrag();
		}
	}

	protected onFreeSpinStarted(): void {
		this._isFreeSpins = true;
		this.disableDrag();
	}

	protected onFreeSpinFinished(): void {
		this._isFreeSpins = false;

		if (this.isAutoSpinning && this._isDragSettingActive) {
			return;
		}

		if (this._canSpin) {
			this.enableDrag();
		}
	}

	protected onSettingsToggleDragReelOff(): void {
		this.view.dragReelToSpinActive = false;
	}

	protected onSettingsToggleDragReelOn(): void {
		this.view.dragReelToSpinActive = true;
	}

	protected onBonusGameWon(): void {
		this._isBonusGame = true;
	}

	protected onBonusGameEnd(): void {
		this._isBonusGame = false;
	}

	protected onInfoPopupClosed(eventId: string, eventType: InfoPopupEvents): void {
		if (eventType === InfoPopupTypes.NO_MONEY) {
			this.enableDrag();
		} else if (eventType === InfoPopupTypes.RESTORE) {
			this.disableDrag();
		}
	}

	protected onNotEnoughMoney(): void {
		this.disableDrag();

		if (this.isSwipeSpin) {
			this.view.resetPosition();
			this.isSwipeSpin = false;
		}
	}

	protected onAllReelsDropOutFinished(): void {
		if (this.isSwipeSpin) {
			this.view.resetPosition();
			this.isSwipeSpin = false;
		}
	}

	protected onAllReelsStopped(): void {
	}

	protected updateLevel(): void {
	}

	protected onCanSpin(): void {
		this._canSpin = true;

		if (this.isAutoSpinning && this._isDragSettingActive) {
			return;
		}

		this.enableDrag();
	}

	protected onStartAutoplayButtonPressed(): void {
		this.isAutoSpinning = true;
		this.disableDrag();
	}

	protected onStopAutoplayButtonPressed(): void {
		this.isAutoSpinning = false;

		if (!this._isBonusGame && this._canSpin) {
			this.enableDrag();
		}
	}

	protected onSpinButtonPressed(): void {
		this.disableDrag();
	}

	protected enableDrag(): void {
		if (!this._isFreeSpins) {
			this.view.canDrag = true;
		}
	}

	protected disableDrag(): void {
		this._canSpin = false;
		if (!this.isSwipeSpin) {
			this.view.forceStopDrag();
		}
		this.view.canDrag = false;
	}

	protected emitStartSpin(): void {
		this.disableDrag();
		this.isSwipeSpin = true;

		this.eventManager.emit(SwipeInteractionEvents.START_SPIN);
	}

	public set isAutoSpinning(value: boolean) {
		this._isAutoSpinning = value;
	}

	public get isAutoSpinning(): boolean {
		return this._isAutoSpinning;
	}

	public set isSwipeSpin(value: boolean) {
		this._isSwipeSpin = value;
	}

	public get isSwipeSpin(): boolean {
		return this._isSwipeSpin;
	}

	public set isDragSettingActive(value: boolean) {
		this._isDragSettingActive = this.view.dragReelToSpinActive = value;
	}

	public get isDragSettingActive(): boolean {
		return this._isDragSettingActive;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionModule.ts